import * as React from 'react';
import { useEffect } from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Decimal128 } from 'proposal-decimal';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { InputNumber } from '~shared/components/InputNumber/InputNumber';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { BillingPeriod } from '~teacher/components/PricingTable/BillingPeriod';
import { formatPrice } from '~teacher/components/PricingTable/PriceComponent/helpers';
import { useTieredPricing } from '~teacher/components/PricingTable/PriceComponent/hooks';
import { MonthSeatPrice } from '~teacher/components/PricingTable/PriceComponent/MonthSeatPrice';
import { PrimaryButton } from '~teacher/components/PricingTable/PrimaryButton';
import { useTeams } from '~teacher/hook/team';
import { seatCountAtom } from '~teacher/pages';

import platform from '../../../../../../../shared/Platform';

function ProPriceComponentLoading() {
  return null;
}

interface ProPriceComponentDataProps {
  billingPeriod: BillingPeriod;
}

function ProPriceComponentData({ billingPeriod }: ProPriceComponentDataProps) {
  const isApp = platform.isWrapperApp();

  const [seats, setSeats] = useAtom(seatCountAtom);

  const pricesQuery = useTieredPricing({
    planType: PlanType.Pro,
    billingPeriod,
    seatCount: seats,
  });

  if (!pricesQuery.data) {
    // Return a spinner or something
    return null;
  }

  const { current, previous } = pricesQuery.data;

  return (
    <>
      <div className={'flex flex-row'}>
        <MonthSeatPrice price={current[BillingPeriod.Monthly]} />
      </div>
      <div
        className={
          'flex flex-wrap gap-x-1 text-base font-sans text-blue-900 leading-8'
        }
      >
        {previous?.[BillingPeriod.Yearly] ? (
          <PreviousPrice
            seats={seats}
            previousYearly={previous[BillingPeriod.Yearly]}
          />
        ) : null}
        <CurrentPrice
          billingPeriod={billingPeriod}
          seats={seats}
          currentYearly={current[BillingPeriod.Yearly]}
        />
      </div>
      <div className="flex gap-1  pb-4">
        <span className="text-blue-900 text-base font-sans leading-8">
          {seats === 1
            ? translate('For {0} Seat').format(seats)
            : translate('For {0} Seats').format(seats)}
        </span>{' '}
        {seats >= 2 ? (
          <Badge tone="neutral">{translate('Save {0}').format('10%')}</Badge>
        ) : null}
      </div>
      <div className="pb-4">
        {isApp ? null : (
          <InputNumber
            id="seats"
            min={1}
            max={500}
            label={translate('Seats')}
            name="seats"
            value={seats}
            onChange={setSeats}
          />
        )}
      </div>
      <div className="row-start-6">
        {isApp ? null : (
          <PrimaryButton
            seats={seats}
            targetPlan={PlanType.Pro}
            billingPeriod={billingPeriod}
          />
        )}
      </div>
    </>
  );
}

interface ProPriceComponentProps {
  billingPeriod: BillingPeriod;
}
const DEFAULT_PRO_SEAT_COUNT = 2;

export function ProPriceComponent({ billingPeriod }: ProPriceComponentProps) {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const setSeatCount = useSetAtom(seatCountAtom);

  const seatCountQuery = useTeams({
    select: (teams) => {
      const team = teams.find(
        (team) =>
          team.id === selectedTeamId && team.subscription?.plan === PlanType.Pro
      );
      return team?.subscription?.totalSeats ?? DEFAULT_PRO_SEAT_COUNT;
    },
  });

  useEffect(() => {
    if (seatCountQuery.data !== undefined) {
      setSeatCount(seatCountQuery.data);
    }
  }, [seatCountQuery.data, selectedTeamId, setSeatCount]);

  if (seatCountQuery.data === undefined) {
    return <ProPriceComponentLoading />;
  }

  return <ProPriceComponentData billingPeriod={billingPeriod} />;
}

interface PreviousPriceProps {
  previousYearly: Decimal128;
  seats: number;
}
function PreviousPrice({ previousYearly, seats }: PreviousPriceProps) {
  return (
    <span className="text-grey-900 line-through">
      {formatPrice(previousYearly.multiply(new Decimal128(seats)))}
    </span>
  );
}

interface CurrentPriceProps {
  currentYearly: Decimal128;
  seats: number;
  billingPeriod: BillingPeriod;
}

function CurrentPrice({
  currentYearly,
  seats,
  billingPeriod,
}: CurrentPriceProps) {
  const price = currentYearly.multiply(new Decimal128(seats));

  if (billingPeriod === BillingPeriod.Yearly) {
    return (
      <span>{translate('{0} Billed Annually').format(formatPrice(price))}</span>
    );
  }

  if (billingPeriod === BillingPeriod.Monthly) {
    return <span>{translate('{0} Annually').format(formatPrice(price))}</span>;
  }
}
